import { useMutation } from '@tanstack/react-query';
import type { Product } from '../types/product';
import type { LangStrings } from '../i18n/en';
import { generateDocumentPdfBlob } from '../utils/generatePdf';
import { API_BASE, ApiError, getCsrfToken, getCurrentLang } from '../lib/api';

interface ProductEmailParams {
  productId: number;
  product: Product;
  recipients: string[];
  subject: string;
  body: string;
  strings?: LangStrings;
  contactPerson?: string;
  companyName?: string;
  companyWebsite?: string;
}

interface ProductEmailResponse {
  message: string;
}

async function sendProductEmail(params: ProductEmailParams): Promise<ProductEmailResponse> {
  const { blob, fileName } = await generateDocumentPdfBlob(
    [params.product],
    params.product.name,
    params.strings,
    params.contactPerson,
    params.companyName,
    params.companyWebsite,
  );

  const formData = new FormData();
  params.recipients.forEach((r) => formData.append('recipients[]', r));
  formData.append('subject', params.subject);
  formData.append('body', params.body);
  formData.append('pdf', blob, fileName);

  const res = await fetch(`${API_BASE}/products/${params.productId}/email`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      'Accept-Language': getCurrentLang(),
      'X-XSRF-TOKEN': getCsrfToken(),
    },
    body: formData,
  });

  if (res.status === 401) {
    localStorage.removeItem('vivatis-logged-in');
    window.location.href = '/login';
    throw new ApiError(401, 'Unauthenticated');
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new ApiError(res.status, body.message ?? 'Request failed', body);
  }

  return res.json();
}

export function useProductEmail() {
  return useMutation({
    mutationFn: sendProductEmail,
  });
}
